import React, {useState} from "react";
import Grid from "@mui/material/Grid";
import {AsyncComponent} from "../common/AsyncComponent";
import {BuildingStatusBadges} from "../BuildingStatusBadges";
import {TestScheduleError} from "../../client/EventScheduleClient";
import {MonitorData} from "../BuildingStatus";
import Form from "react-bootstrap/Form";
import {calendar} from "../../Routes";
import {ArrowUpFromSquare} from "../icons/Icons";
import {useEntitlements} from "../../EntitlementProvider";
import {Permission} from "../../Permission";
import MuiAlert from "@mui/material/Alert";
import {Button, Menu, MenuItem} from "@mui/material";
import {ControlStrategy} from "../../client/BuildingClient";
import {SubmitButton} from "../common/SubmitButton";

interface AsyncDeviceStatusHeaderProps {
    scheduleEvent: (buildingId: number, controlStrategyId?: number) => Promise<TestScheduleError | null>
    receiveMonitorData: (buildingId: number) => Promise<MonitorData>
    buildingId: number
    controlStrategies: ControlStrategy[]
}

const TestScheduleOutcome = ({errorMessages}: { errorMessages?: string[] }) => {
    let alert: JSX.Element[] | JSX.Element
    if (errorMessages) {
        alert = errorMessages.map((m, i) => {
            const id = `error-schedule-event-message-${i}`
            return <MuiAlert variant='standard' severity="warning" key={id} data-testid={id}>{m}</MuiAlert>
        })
    } else {
        alert = <MuiAlert variant='standard' sx={{bgcolor: 'primary'}} data-testid='test-schedule-success-alert' >
            <span>
                <span>Event created. View on calendar</span> <a href={calendar.path} target="_blank" rel="noreferrer" data-testid="go-to-event-calendar-control"><ArrowUpFromSquare/></a>
            </span>
        </MuiAlert>
    }

    return <div data-testid='test-schedule-outcome'>{alert}</div>
}

interface ChooseControlStrategyProps {
    controlStrategies: ControlStrategy[]
    handleSubmit: (controlStrategyId: number) => void
    disabled: boolean
}

const ChooseControlStrategy = ({controlStrategies, handleSubmit, disabled}: ChooseControlStrategyProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (controlStrategyId: number) => {
        handleClose();
        handleSubmit(controlStrategyId)
    }
    return (<><Button onClick={handleOpen} variant={'contained'} disabled={disabled}>5 minute Dispatch</Button>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            {
                controlStrategies.map((strategy, index) => (
                    <div key={index}>
                        <MenuItem onClick={() => handleMenuItemClick(strategy.id)}>{strategy.name}</MenuItem>
                    </div>
                ))
            }
        </Menu></>);
}


export const AsyncDeviceStatusHeader: React.FC<AsyncDeviceStatusHeaderProps> = ({receiveMonitorData, scheduleEvent, buildingId, controlStrategies}) => {
    const entitlements = useEntitlements()

    const [showTestScheduleOutcomeMessages, setShowTestScheduleOutcomeMessages] = useState<boolean>(false)
    const [isSubmittingTestSchedule, setIsSubmittingTestSchedule] = useState<boolean>(false)
    const [scheduleEventErrorMessage, setScheduleEventErrorMessage] = useState<TestScheduleError | null>(null)

    async function handleSubmit(controlStrategyId?: number) {
        setIsSubmittingTestSchedule(true)
        setScheduleEventErrorMessage(await scheduleEvent(buildingId, controlStrategyId))
        setIsSubmittingTestSchedule(false)
        setShowTestScheduleOutcomeMessages(true)
    }

    return (<Grid container justifyContent="space-between" alignItems="center">
        {/* Left side: title + chips */}
        <Grid item>
            <Grid container alignItems="center" spacing={2}>
                <Grid item>
                    <span>Site Consumption</span>
                </Grid>
                <Grid item>
                    <AsyncComponent loaderFunction={() => receiveMonitorData(buildingId)} dataPropName={"data"} loaderInterval={5000} effectDependency={[buildingId]}>
                        <BuildingStatusBadges
                            data={{lastHeartbeats: [], latestMeterReadings: []}}
                        />
                    </AsyncComponent>
                </Grid>
            </Grid>
        </Grid>
        {/* Right side: button */}
        {entitlements.has(Permission.WRITE_EVENT) && <Grid item>
            <Form.Group> {
                controlStrategies.length > 1 ?
                    <ChooseControlStrategy controlStrategies={controlStrategies} handleSubmit={handleSubmit} disabled={isSubmittingTestSchedule}/>
                    :
                    <SubmitButton dataTestId={'schedule-event'} onSubmit={() => handleSubmit(undefined)} disabled={isSubmittingTestSchedule}>
                        5 minute Dispatch
                    </SubmitButton>
            }
            </Form.Group>
        </Grid>}
        { showTestScheduleOutcomeMessages &&
            <Grid item>
                <TestScheduleOutcome errorMessages={scheduleEventErrorMessage?.messages}/>
            </Grid>
        }
    </Grid>);
}